import {Injectable} from '@angular/core';
import {
  HttpBackend,
  HttpClient,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpParams,
  HttpRequest
} from '@angular/common/http';
import {EMPTY, Observable, of, switchMap, throwError} from 'rxjs';
import {CommonService} from "./common.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ActivatedRoute, Router} from "@angular/router";
import {Store} from "@ngrx/store";
import * as fromRoot from "../../../state/app.state";
import {catchError, map} from "rxjs/operators";
import {v1URL} from "../constants/urls.constants";
import * as moment from 'moment';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  messageQueue = [];
  processingMessage = false;
  excludedUrlsRegex: RegExp[];
  excludedUrls = ['.svg'];

  constructor(
    private commonService: CommonService,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private store: Store<fromRoot.State>,
    private router: Router,
    private backendHandler: HttpBackend,
  ) {
    this.excludedUrlsRegex =
      this.excludedUrls.map(urlPattern => new RegExp(urlPattern, 'i')) || [];
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const passThrough: boolean =
      !!this.excludedUrlsRegex.find(regex => regex.test(req.url));

    if (passThrough) {
      return next.handle(req);
    }

    let headers = req.headers;

    const body = req.body;
    let requestToSend: HttpRequest<any>;

    return of(true).pipe(
      switchMap(() => {
        if (this.commonService.isAuthenticated()) {
          const authenticationToken = this.commonService.getAuthenticationToken();

          const tokenDetails = JSON.parse(atob(authenticationToken.split('.')[1]));

          if (moment(new Date(tokenDetails.exp * 1000)).subtract(2, 'm').isBefore(new Date())) {
            if (this.commonService.getRefreshToken()) {
              const http = new HttpClient(this.backendHandler);
              let payload = new HttpParams();
              payload = payload.append("refresh_token", this.commonService.getRefreshToken());
              payload = payload.append("grant_type", 'refresh_token');

              try {
                return http.post(v1URL.login.url, payload, {
                  headers: new HttpHeaders({
                    'Content-Type': 'application/x-www-form-urlencoded'
                  })
                }).pipe(
                  map((data) => data),
                  catchError(() => {
                    this.commonService.logout();
                    return EMPTY;
                  })
                )
              } catch (error) {
                this.commonService.logout();
              }
            } else {
              this.commonService.logout();
            }
          }
        }


        return of({})
      }),
      switchMap((response: any) => {
        if (response?.access_token && response?.refresh_token) {
          this.commonService.setAuthenticationToken(response.access_token);
          this.commonService.setRefreshToken(response.refresh_token);
        }

        if (this.commonService.getAuthenticationToken()) {
          headers = headers.append('Authorization', 'Bearer ' + this.commonService.getAuthenticationToken());
        }

        requestToSend = req.clone({headers, body});

        return next.handle(requestToSend).pipe(
          map((response) => {
            if (
              (response['status'] && response['status'] !== 200)
            ) {
              throw response;
            }

            return response;
          }),
          catchError((errorResponse) => this.handleError(errorResponse))
        );
      })
    )
  }

  handleError(response) {
    if (response.status === 401) {
      const currentUrl = this.router.url;
      if (currentUrl.includes('login')) {
        return null;
      }

      const url = currentUrl.split('?')[0];

      const urlArr = url.split('/');

      this.router.navigate([`${urlArr[1]}/${urlArr[2] === 'm' ? 'm/' : ''}not-found`]);

      return null;
    }

    if (response.status === 403) {
      this.commonService.clearAppState();
      this.commonService.clearStorage();
      this.router.navigate(['/']);
    }

    const errorObject =
      (response && response.error) ||
      (response && response.body) ||
      {error_description: 'Oops! Something went wrong.'};

    this.addMessageToQueue(errorObject.error_description || errorObject.message);

    return throwError(errorObject);
  }

  addMessageToQueue(message) {
    this.messageQueue.push(message);

    if (!this.processingMessage) {
      this.displaySnackbar();
    }
  }

  displaySnackbar() {
    const nextMessage = this.getNextMessage();

    if (!nextMessage) {
      this.processingMessage = false; // No message in the queue, set flag false
      return;
    }

    this.processingMessage = true; // A message was dequeue and is being processed

    this.snackBar
      .open(nextMessage, `X`, {
        duration: 5000,
        verticalPosition: 'top',
        horizontalPosition: 'center',
        panelClass: 'danger',
      })
      .afterDismissed()
      .subscribe(() => {
        this.displaySnackbar();
      });

    this.snackBar._openedSnackBarRef?.onAction().subscribe(() => {
      this.snackBar._openedSnackBarRef?.dismiss();
    });
  }

  getNextMessage(): string | undefined {
    return this.messageQueue.length ? this.messageQueue.shift() : null;
  }
}
